import { Container, Flex, Text } from "@mantine/core";
import { type JSX, useEffect } from "react";
import { useVerifiedCustomerStore } from "@/customer-info";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { useEligibilityV2 } from "@/sales-tools/home-product/hooks/useEligibilityV2";
import { useProductOfferPolling } from "@/sales-tools/home-product/hooks/useProductOfferPolling";
import { MultiProduct } from "@/sales-tools/multi-product/MultiProduct";
import { UnifiedSalesView } from "@/sales-tools/unified-sales-view/UnifiedSalesView";
import { usePartner, useSessionId, useTypedMostRecentTask } from "@/sdk";
import { useProductOfferStore } from "./home-product";
import { IneligibleIcon, LoadingAnimation } from "./home-product/components/Icons";
import { useQuerySelectedProductOffer } from "./home-product/hooks/useQuerySelectedProductOffer";
import classes from "./SalesFlow.module.css";
import { getReservationSid, parsePhoneNumberAttributes } from "./utils";

export function SalesFlow(): JSX.Element {
    useQuerySelectedProductOffer();
    useProductOfferPolling();

    const {
        task: mostRecentTask,
        isVoice,
        isChat,
        voiceTask,
        chatTask,
        customerInteractionId,
    } = useTypedMostRecentTask();
    const partner = usePartner();

    if (!isVoice && !isChat) {
        throw new Error("Active task is not voice or chat task");
    }
    const { isUnifiedSalesViewEnabled } = usePartnerFeatureConfigured();
    const showUnifiedSalesView = isUnifiedSalesViewEnabled || isChat;

    const sessionId = useSessionId();
    const customer = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer);
    const storesHaveFinishedRehydrating = useProductOfferStore.persist.hasHydrated();
    const storesHaveRehydratedData = useProductOfferStore((state) => state.getProducts(sessionId).length > 0);
    const shouldRefetchEligibilityForSession = storesHaveFinishedRehydrating && !storesHaveRehydratedData;
    const {
        data: eligibilityV2,
        isError: eligibilityV2Error,
        isLoading: isLoadingEligibilityV2,
    } = useEligibilityV2(
        {
            // TODO: remove `asurionCallId` after https://github.com/Soluto-Private/expert-workspace-api/pull/2240 is deployed to prod
            asurionCallId: isVoice && voiceTask ? voiceTask.asurionCallId : undefined,
            customerInteractionId,
            partner,
            reservationSid: getReservationSid(mostRecentTask) ?? sessionId,
            sessionId,
            ...parsePhoneNumberAttributes(customer?.mdn),
            address: customer?.address,
            eligibilityAttributes: {
                currentPlan: customer?.productName,
                isInternationalCaller: customer?.metadata?.isInternationalCaller,
                chatSourceApp: isChat ? chatTask?.sourceApp : undefined,
                currentSubscriptions: customer?.currentSubscriptions,
            },
            salesChannel: mostRecentTask?.type,
        },
        shouldRefetchEligibilityForSession,
    );

    useEffect(() => {
        if (shouldRefetchEligibilityForSession && eligibilityV2) {
            useProductOfferStore.getState().updateProducts(sessionId, eligibilityV2);
        }
    }, [eligibilityV2, sessionId, shouldRefetchEligibilityForSession]);

    if (eligibilityV2Error) {
        return (
            <Flex className={classes.loadingContainer} data-testid="failure">
                <Container>{IneligibleIcon}</Container>
                <Text className={classes.statusBodyHeader}>We had an issue checking eligibility.</Text>
                <Text className={classes.statusBodyText} data-testid="sale-view-status-body">
                    Try refreshing the page.
                </Text>
            </Flex>
        );
    }

    if (isLoadingEligibilityV2) {
        return (
            <Flex className={classes.loadingContainer}>
                <Text size="sm">Confirming customer information...</Text>
                <Container maw="160px">
                    <LoadingAnimation />
                </Container>
            </Flex>
        );
    }

    return showUnifiedSalesView ? <UnifiedSalesView isChat={isChat} /> : <MultiProduct />;
}
